 
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";

import {useUser} from "@hooks/use-user";
import {AuthCardLayout} from "@layouts/AuthCardLayout";

export function LogoutPage() {
    useEffect(() => {
        document.title = "Logout - Pecunio Agents";
    }, []);

    const {logout} = useUser();
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleLogout = async () => {
            try {
                // Force client-side cleanup regardless of server response
                const cleanupClientSession = () => {
                    // Clear any auth-related cookies (except CSRF which is needed for future requests)
                    const cookies = document.cookie.split(';');
                    for (const cookie of cookies) {
                        const cookieName = cookie.split('=')[0].trim();
                        if (cookieName !== 'csrftoken') {
                            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                        }
                    }
                    
                    // Clear any auth data from localStorage
                    localStorage.removeItem('user');
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('session');
                };
                
                // Set a timeout to ensure we don't wait forever for the server response
                const timeoutPromise = new Promise<{success: true}>((resolve) => {
                    setTimeout(() => {
                        cleanupClientSession();
                        resolve({success: true});
                    }, 2000); // 2 second timeout
                });
                
                // Race between the actual logout request and the timeout
                await Promise.race([logout(), timeoutPromise]);

                // Always clean up client-side session data regardless of server response
                cleanupClientSession();
                
                // We'll consider the logout successful as long as we've cleaned up the client session
                // This ensures users can always log out even if the server has issues
            } catch {
                // Even with an error, we'll force client-side cleanup
                // Clear any auth-related cookies and localStorage
                const cookies = document.cookie.split(';');
                for (const cookie of cookies) {
                    const cookieName = cookie.split('=')[0].trim();
                    if (cookieName !== 'csrftoken') {
                        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                    }
                }
                localStorage.removeItem('user');
                localStorage.removeItem('authToken');
                localStorage.removeItem('session');
            } finally {
                setIsLoggingOut(false);
                // Always show success UI since we've cleaned up the client session
                setError(null);
                // Redirect to login page after a brief delay
                setTimeout(() => {
                    navigate('/login');
                }, 1500);
            }
        };

        handleLogout();
    }, [logout, navigate]);

    return (
        <AuthCardLayout>
                <div className="text-center">
                    {isLoggingOut ? (
                        <>
                            <div
                                className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                            <p className="text-muted-foreground">Logging out...</p>
                        </>
                    ) : error ? (
                        <>
                            <div className="text-red-500 mb-4">
                                <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                                          clipRule="evenodd"/>
                                </svg>
                            </div>
                            <h1 className="text-xl font-semibold text-red-600 mb-2">Logout Error</h1>
                            <p className="text-muted-foreground mb-4">{error}</p>
                            <p className="text-sm text-muted-foreground">Redirecting to login page...</p>
                        </>
                    ) : (
                        <>
                            <div className="text-green-500 mb-4">
                                <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd"
                                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L7.53 10.91a.75.75 0 00-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                          clipRule="evenodd"/>
                                </svg>
                            </div>
                            <h1 className="text-xl font-semibold text-green-600 mb-2">Logged Out</h1>
                            <p className="text-muted-foreground mb-4">You have been successfully logged out.</p>
                            <p className="text-sm text-muted-foreground">Redirecting to login page...</p>
                        </>
                    )}
                </div>
        </AuthCardLayout>
    );
}